// client/src/features/catalogue/costPriceTiers.ts
//
// THE COST PRICE OF ONE CATALOGUE ROW, IN TT$, AT EACH UNIT THE SCREEN OFFERS.
//
// ── Why this is a module and not four closures inside the page ───────────────
// It is money arithmetic with four branches and a rounding rule, and it was
// living inside `MasterCataloguePage.tsx` where nothing could test it. It is
// also called four times per visible row on every render and every sort, so
// computing all four tiers in ONE pass per row is both the cheaper and the
// clearer shape.
//
// ── WHAT THIS FILE IS NOT ────────────────────────────────────────────────────
// It is NOT a second copy of "how many packs are in a case". That ranking has
// exactly one owner — `masterCatalogueVendorSync.mapRowToAiCandidate` on the
// server — and the answer arrives on the row as `packFacts`, resolved by
// `masterCatalogueItem.service.packFactsFor`. This module divides by the number
// it is handed and never goes looking for one. Reading `units_per_case` here
// (which is what the page used to do) is precisely the bug: that column is the
// HAND-ENTRY column and is null on every vendor-invoice row, so "Per Pack" read
// "—" on ~98% of the catalogue, including rows whose unit column plainly said
// "C-12" — a case of twelve. See TECH_DEBT #138, which closes by naming the
// blank screen a UI question rather than a data one.
//
// ── WHY EVERYTHING STAYS IN TT$ ──────────────────────────────────────────────
// `Cost Price` and `Per Case (TT$, w/ VAT)` are the same number and once
// disagreed by cents, because one of them detoured TT$ → USD → TT$ and
// `ceilPrice` rounds UP at both hops (315.61 against 315.57 for one 280.50 +
// 12.5% line). The per-pack tier had the same detour and now does not: it is
// the TT$ case figure divided by the pack count, so `Per Pack × pack count`
// reconciles against `Per Case` the way a human checking the row expects.
//
// The USD fallback at the bottom is the pack size read out of the item NAME
// (`getAlternateQuotationPricing`), which is USD-native and is unchanged — it
// is the last resort, below everything the row actually states.

import { ceilPrice } from '../../utils/priceUtils';
import { usdCostFacts } from '../../utils/fxDisplay';
import { canonicalUnit, isGroupingUnit, isSingleItemUnit, measureFromText, splitSizeText } from '../../utils/unitVocabulary';
import { normalizeConversions, restatesOwnUnit, shownConversions } from '../../utils/unitConversions';
import { KG_PER_UNIT, LB_PER_KG, LITRE_PER_UNIT, LITRES_PER_GALLON, ounceLitres } from '../../utils/packConversion';
import { statedLitrePerOunce } from '../../utils/unitConversionGraph';
// The name-text derivation `altPricingFor` below feeds into `costPriceTiersTtd`
// as its `alt` argument — see `getAlternateQuotationPricing`'s own doc comment.
import { getAlternateQuotationPricing, type AlternateQuotationPricing } from '../../utils/quotationUnitConversions';

/**
 * `priceLevels.byUnit` as the screens may read it: the stored entries minus any a
 * row that only restates the item's own unit manufactured (QUO-00199 — "1 PCS = 1
 * LBS" on an LBS item stored PCS at the per-LB price, before the server's price
 * graph learned to skip such rows). Same predicate as the server
 * (`restatesOwnUnit`), applied at read time so no stored data is migrated. A unit
 * a real, shown conversion also names keeps its entry.
 */
export function shownByUnit<T extends { unit?: string | null }>(item: {
  unit?: string | null;
  unitConversions?: unknown;
  priceLevels?: { byUnit?: T[] | null } | null;
}): T[] {
  const byUnit = item.priceLevels?.byUnit || [];
  const restated = new Set<string>();
  normalizeConversions(item.unitConversions).forEach((c) => {
    if (restatesOwnUnit(c, item.unit)) restated.add(canonicalUnit(c.fromUnit) || "");
  });
  if (restated.size === 0) return byUnit;
  shownConversions(item.unitConversions, item.unit).forEach((c) => {
    restated.delete(canonicalUnit(c.fromUnit) || "");
    restated.delete(canonicalUnit(c.toUnit) || "");
  });
  return byUnit.filter((e) => !restated.has(canonicalUnit(e?.unit || "") || ""));
}

/**
 * The units a catalogue row can be priced at.
 *
 * Named for the Master Catalogue's `Cost Price (TT$)` selector, which is gone —
 * its four options each printed a figure one of the four tier columns beside it
 * already printed, and the owner read the repetition off the screen on
 * 2026-09-08. The type stays: it is what `costUnitOptions` keys the quotation
 * catalogue popup's selector on, and that one is not a duplicate of anything.
 */
export type CostUnit = 'case' | 'pack' | 'kg' | 'lb' | 'l' | 'gal';

/** Everything a tier calculation reads off a catalogue row. */
export interface CostTierItem {
  item_price?: number | null;
  currency?: string | null;
  /** Which writer created the row. Read only by `catalogueRowIsTtd` — see there. */
  source?: string | null;
  /** The unit `item_price` is PER, as the row states it. See `baseUnitSuffix`. */
  unit?: string | null;
  vat_applicable?: boolean | null;
  /**
   * The VAT rate this row states, percent (`models/MasterCatalogue.js`). NO
   * LONGER READ for cost — owner decision 2026-09-24 ("one Trinidad VAT
   * rate"): a `vat_applicable` row is always grossed at the one Settings rate
   * (`getVatRate`, `lib/settingsApi.ts`), never at what this field states.
   * Kept on the type only because the server model still carries it.
   */
  vat_rate?: number | null;
  /**
   * A collapsed "Assorted X" row (`getGroupedItems`). Its `finalPrice` is the
   * group price, TT$ and VAT-inclusive already — see `costPriceTiersTtd`.
   */
  isGroup?: boolean;
  finalPrice?: number | null;
  unit_size?: string | null;
  priceLevels?: {
    // `number | null`, matching `ttPerL` beside them and the Mongo schema
    // itself (`MasterCatalogue.priceLevels.ttPerKg`/`ttPerLb`: `default:
    // null`, invariant 8 — "NULL, not 0... a row written without a
    // priceLevels block reads the same way as one written with an
    // underivable tier"). Widened 2026-09-25 wiring `SyncPreviewCard.priceFacts`
    // (`lib/vendorInvoiceApi.ts`), which sends the server's own explicit
    // `null` for a tier a row does not state — `positive()` below already
    // reads `null` the same as `undefined`, so nothing here changes; only the
    // type now says what the data already does.
    ttPerLevel2?: number | null;
    ttPerLevel1?: number | null;
    ttPerKg?: number | null;
    ttPerLb?: number | null;
    ttPerL?: number | null;
    /** TT$ w/ VAT per unit the item's own conversions reach (`costEngine.computeGraphPriceLevels`). */
    byUnit?: Array<{ unit?: string | null; ttAmount?: number | null }> | null;
  } | null;
  /** Server-resolved pack facts. See `packFactsFor` — do NOT re-derive these. */
  packFacts?: { packCount?: number | null; unitSize?: string | null } | null;
  /** The row's own conversions: where an ounce size learns its litres (D170.4). */
  unitConversions?: unknown;
}

/**
 * The per-unit figures derived from the item NAME by
 * `getAlternateQuotationPricing`. USD, and VAT-EXCLUSIVE — this module grosses
 * them up, matching what the page did before.
 */
export interface AltUnitPricesUsd {
  perInnerUsd?: number | null;
  perKgUsd?: number | null;
  perLbUsd?: number | null;
  perLitreUsd?: number | null;
}

export interface CostTiersTtd {
  case: number | null;
  pack: number | null;
  kg: number | null;
  lb: number | null;
  /** Per litre (prod USC-BUG-0013 / 0014 / 0018). See the PER LITRE block below. */
  l: number | null;
  /** Per gallon: the per-litre figure times `LITRES_PER_GALLON` (MC-2, D28 / D36). */
  gal: number | null;
  /**
   * Where the per-pack figure came from, so the screen can label it honestly.
   *
   * `item_name` is the only one that licenses printing
   * `getAlternateQuotationPricing`'s `innerUnitLabel` / `basisHint` beside the
   * price — those describe a pack read out of the item's NAME, and putting
   * "per derived unit" under a figure that actually came off the row's own
   * stated pack count says the wrong thing about a number on a cost screen.
   */
  packBasis: 'stored_tier' | 'row_pack_count' | 'item_name' | null;
}

/** A stored tier counts only when it is a real, positive number. 0 means "never written". */
function positive(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * IS THIS ROW'S `item_price` STATED IN TT$?
 *
 * One rule, because three screens were asking it and answering differently, and
 * the answer is a factor of ~6.5 on a cost price:
 *
 *   - the server (`masterCatalogueVendorSync.mapRowToAiCandidate`):
 *     `row.currency || (source === 'vendor_invoice' ? 'TTD' : 'USD')`
 *   - `MasterCataloguePage` / this module, before this: `currency === 'TTD'`
 *     only — so a LEGACY vendor-invoice row, whose `currency` column is
 *     `default: null` and was never written, was read as US$ and multiplied by
 *     the rate. The catalogue screen showed 6.5x the supplier's price.
 *   - `MasterCatalogueSearchPopup` / `QuotationDetailPage`:
 *     `currency === 'TTD' || source === 'vendor_invoice'` — which has the
 *     opposite failure, on a vendor invoice genuinely raised in US$
 *     (`vendorInvoiceCurrency` resolves those and the sync stores the code):
 *     that row's US$ price was DIVIDED by the rate.
 *
 * The server's is the one that is right in both directions and it is the one
 * kept: an explicitly stated currency always wins, and only an unstated one
 * falls back on what the row's writer deals in. USC's vendor invoices are TT$
 * unless the invoice says otherwise (invariant 5).
 */
export function catalogueRowIsTtd(item: CostTierItem): boolean {
  const stated = String(item.currency || '').trim().toUpperCase();
  if (stated) return stated === 'TTD';
  return String(item.source || '').trim() === 'vendor_invoice';
}

/** What `altPricingFor` reads off a row, on top of what feeds the tiers. */
export interface AltPricingItem extends CostTierItem {
  item_name?: string | null;
}

/**
 * THE "PER PACK, READ OUT OF THE ITEM NAME" DERIVATION — ONE PLACE, not two.
 *
 * `MasterCataloguePage` built this per row: `item_price` turned into a US$
 * figure (`catalogueRowIsTtd`-aware — TT$ rows divide by the rate, US$ rows
 * do not), fed into `getAlternateQuotationPricing` alongside the row's unit,
 * name and `pricingDetails`. The vendor-invoice sync preview card passed
 * `alt = null` into `costPriceTiersTtd` instead of reading this off its own
 * `priceFacts`, so a line the sync would price per pack purely off its NAME
 * ("CORN FLAKES 24 X 300G", CASE, TT$240, no pack facts stated) showed the
 * grid's Per Pack TT$10.01 and nothing on the card (the owner, 2026-09-25).
 *
 * Extracted so both read the SAME derivation and cannot drift again — the
 * grid's own call is now a one-line delegate to this, its result unchanged.
 *
 * @param pricingDetails — the row's `meta_data.pricingDetails` (the grid) or
 *                          `priceFacts.pricingDetails` (the card): the
 *                          server's `displayEquivalents` snapshot
 *                          `getAlternateQuotationPricing` reads first, before
 *                          it ever pattern-matches the name.
 * @returns null exactly where the grid would pass `altPricing: null` into
 *          `costPriceTiersTtd`: no `item_price` at all, or a TT$-priced row
 *          with no exchange rate loaded yet to divide down to US$. A
 *          US$-priced row needs no such conversion and is read as-is.
 */
export function altPricingFor(
  item: AltPricingItem,
  usdToTtd: number | null,
  pricingDetails?: Record<string, unknown> | null
): AlternateQuotationPricing | null {
  const p = item.item_price;
  if (typeof p !== 'number' || !Number.isFinite(p)) return null;
  // A collapsed group's price is already VAT-inclusive and is no pack: `perInnerUsd`
  // would be grossed again by the caller (`costPriceTiersTtd`, `fromUsd`).
  if (item.isGroup) return null;
  const usd = catalogueRowIsTtd(item) ? (usdToTtd == null ? null : ceilPrice(p / usdToTtd)) : ceilPrice(p);
  if (usd == null) return null;
  return getAlternateQuotationPricing(usd, item.unit || 'pcs', item.item_name || '', pricingDetails ?? undefined);
}

/**
 * T&T's standard VAT rate, percent — admin-changeable in Settings, and no
 * longer a literal anywhere in this file.
 *
 * Owner decision (2026-09-24, "one Trinidad VAT rate"): the government can
 * change VAT, so no function below hardcodes a percentage or reads a row's
 * own `vat_rate` — both used to happen here (a local
 * `DEFAULT_TT_VAT_RATE_PERCENT = 12.5` fallback, taken over by a row's stated
 * `vat_rate` when that was a finite number). A catalogue row's VAT is its
 * boolean `vat_applicable` only; when it is `true`, the rate that grosses the
 * price is ALWAYS the one read from `GET /api/settings/vat-rate`
 * (`lib/settingsApi.getVatRate`), passed in by every caller below. `null`
 * means the rate has not loaded, or no admin has ever set one — and every
 * function refuses to gross a VAT-applicable row rather than guess, the same
 * refusal `usdToTtd == null` already gets a few lines down.
 */

/** Everything `catalogueRowCostPrice` reads off a row. */
export interface CatalogueRowCostPriceItem {
  item_price?: number | null;
  vat_applicable?: boolean | null;
  /**
   * Accepted on the type ONLY so a real `MasterCatalogueItem` (which still
   * carries the server field) can be passed straight in — NEVER READ. See
   * `catalogueRowCostPrice`'s own doc comment: cost is always grossed at the
   * passed-in Settings rate, whatever this states.
   */
  vat_rate?: number | null;
}

/**
 * A CATALOGUE ROW'S `item_price`, PLUS VAT WHEN THE ROW CARRIES IT.
 *
 * The one owner of "item_price plus VAT when vat_applicable" — a rule that
 * used to live as two independent hardcoded `* 1.125`s in this file (this
 * row's `withVat` below, and `costUnitOptions`'s now-removed `vatFactor`).
 *
 * Missing ≠ zero (invariant 8): a null/non-finite `item_price` is null out,
 * never 0. `vat_applicable !== true` returns the price untouched — this is
 * ONE answer for `false` and for a blank/unset row: a row whose VAT is not
 * yet known is priced without VAT until someone answers the "Does this item
 * have VAT?" popup (`features/quotations/CatalogueVatPrompt.tsx`,
 * `pricingDetails.catalogueVatUnset` marks a line priced this way). VAT is
 * never inferred, only stated.
 *
 * @param vatRatePercent — the Settings VAT rate (`getVatRate`), percent, or
 *   `null` while it has not loaded or has never been set. A `vat_applicable`
 *   row with no rate to gross at returns `null` — never a guessed figure. The
 *   caller shows "VAT rate is not set in Settings" (server code
 *   `VAT_RATE_NOT_SET`) rather than a silently wrong cost.
 */
export function catalogueRowCostPrice(
  item: CatalogueRowCostPriceItem,
  vatRatePercent: number | null
): number | null {
  const price = item.item_price;
  if (typeof price !== 'number' || !Number.isFinite(price)) return null;
  if (!item.vat_applicable) return price;
  if (typeof vatRatePercent !== 'number' || !Number.isFinite(vatRatePercent)) return null;
  return price * (1 + vatRatePercent / 100);
}

/**
 * DOES THIS ROW NEED THE SETTINGS VAT RATE TO PRICE AT ALL, AND HAS IT NOT LOADED?
 *
 * The ONE test for "refuse rather than guess" on a `vat_applicable` row with
 * no rate — used by `costPriceTiersTtd` itself (every tier refuses on this),
 * and exported so every CALLER that only needs the boolean, to gate a further
 * step or to word a refusal message, reads it here instead of re-deriving
 * `item.vat_applicable && (typeof vatRatePercent !== 'number' || ...)` a
 * second (or third) time. Three call sites carried that exact expression
 * before this existed — `itemLookupRows.catalogueCostUnitOptions`,
 * `itemLookupRows.priceAtUnit`'s refusal wording, and
 * `QuotationDetailPage.applyCataloguePick`'s toast — and a fourth copy is
 * exactly how a future one would silently stop agreeing with the others.
 */
export function catalogueRowNeedsUnsetVatRate(
  item: Pick<CostTierItem, 'vat_applicable'>,
  vatRatePercent: number | null
): boolean {
  return !!item.vat_applicable && (typeof vatRatePercent !== 'number' || !Number.isFinite(vatRatePercent));
}

/**
 * The unit the `case` tier's figure is actually PER, when that is not a case.
 *
 * Reported by the owner on a call: "the case price showed the pound price". It
 * did, and the number was right — a catch-weight line (meat, poultry, fish) is
 * billed per pound, so the row's `unit` is LB and `item_price` is a per-pound
 * figure. What was wrong is the heading: the column said "Per Case" over it.
 *
 * A real case price is NOT derivable for those rows and must not be invented.
 * The vendor sync deliberately refuses to write a "1 BOX = 20 LBS" conversion
 * on a catch-weight line, because that ratio is a fiction that would go on to
 * misprice every quotation touching the row (see `conversionsFromPackFacts`).
 * With no stated ratio there is no case price, so the fix is to say which unit
 * the figure IS per rather than to guess one.
 *
 * Only when the row states a unit the vocabulary does not call a grouping unit
 * — a row that says CASE or BOX needs no suffix, and one that says nothing at
 * all has nothing to add.
 *
 * Shared, not copied: the Master Catalogue screen prints it after the Cost
 * Price figure ("TT$4.50 / LB") and the quotation's catalogue search popup uses
 * it to LABEL the option that feeds a line's cost. A popup option reading "Per
 * Case" over a per-pound price is the same wrong heading, one screen along, and
 * on the side of the system that decides what a client is charged.
 */
export function baseUnitSuffix(item: CostTierItem): string | null {
  const u = String(item.unit || '').trim();
  if (!u || isGroupingUnit(u)) return null;
  return u;
}

/** How much ONE pack holds: kilograms for a mass, litres for a volume. */
export interface PackSize {
  dimension: 'mass' | 'volume';
  /** Kilograms when `dimension` is 'mass', litres when it is 'volume'. */
  amount: number;
  /** An ounce size, in ounces: it also fills litres (D170.4); absent for every other size. */
  ounces?: number;
}

/**
 * Read a pack-size string ("300g", "1.5kg", "5lb", "8oz", "500ml", "1 GAL") as
 * how much one pack holds. Returns null if unparseable.
 *
 * A LITRE IS NEVER A KILOGRAM. This was `parseUnitSizeToGrams`, which read ml
 * and l as grams "(density ≈ 1)", so every liquid row printed a Per kg and a
 * Per lb that were really its price per litre. The owner, 2026-09-14: "1 to 1
 * treating is wrong, discard that completely." A mass answers only in
 * kilograms and a volume only in litres, and each is priced in its own column —
 * the same rule `itemGroup.parseSize` holds on the server.
 *
 * The factors come from `packConversion`'s two tables, not a switch of its own:
 * that is where USC's pound (2.2 to the kilo) and gallon (four litres) are
 * stated, and a literal here once left this screen on a different pound from
 * the stored tiers beside it.
 */
export function readPackSize(unitSize?: string | null): PackSize | null {
  if (!unitSize) return null;
  // The vocabulary owner reads the size, not a unit regex of this file's own —
  // that regex never read "1.9ltrs", "158gr" or "88lbs" (USC-BUG-0014, MC-4).
  // Its unit comes back canonical (LTRS -> L, GR -> G), which is how both
  // tables below are keyed.
  const size = measureFromText(unitSize);
  if (!size) return null;
  const val = parseFloat(size.amount);
  if (!Number.isFinite(val) || val <= 0) return null;
  const unit = size.unit;
  if (unit === 'OZ') return { dimension: 'mass', amount: val * KG_PER_UNIT.OZ, ounces: val };
  if (KG_PER_UNIT[unit] != null) return { dimension: 'mass', amount: val * KG_PER_UNIT[unit] };
  if (LITRE_PER_UNIT[unit] != null) return { dimension: 'volume', amount: val * LITRE_PER_UNIT[unit] };
  return null;
}

/**
 * The VAT-inclusive TT$ case price the invoice sync already worked out and saved
 * on the row, using that invoice's own FX rate.
 *
 * Preferred over deriving it from `item_price`, because that derivation detours
 * TT$ → USD → TT$ and `ceilPrice` rounds up at both hops. Null for grouped rows
 * and for anything written before `priceLevels` existed — those keep the
 * derivation below.
 */
export function storedCaseTtd(item: CostTierItem): number | null {
  return positive(item.priceLevels?.ttPerLevel2);
}

/**
 * Every cost tier this row can honestly state, in TT$, VAT-inclusive.
 *
 * ONE call per row: the four tiers share a case figure, a pack count and a pack
 * size, and computing them together is what stops the page parsing the same
 * size string four times for every row on the page.
 *
 * A tier this row cannot state is `null`, and the caller prints "—". That is
 * deliberate and it is the whole point of the pack-count check: dividing by a
 * pack count of 1 nobody stated would print the CASE price under a "Per Pack"
 * heading — a confidently wrong number where a blank is the true answer.
 *
 * @param usdToTtd — TT$ per US$, or null while the settings rate has not loaded.
 *                   Null means a USD-priced row has NO TT$ figure; it never
 *                   means "assume 6", which is what this screen used to do.
 * @param alt      — the name-text derivation, used only where the row itself
 *                   states nothing.
 * @param vatRatePercent — the Settings VAT rate (`getVatRate`), percent, or
 *                   null while it has not loaded / has never been set. See
 *                   `catalogueRowCostPrice`'s own doc comment: a
 *                   `vat_applicable` row grosses at this rate ONLY, never at
 *                   a rate it states itself, and refuses (null tier) rather
 *                   than guess when this is null.
 */
export function costPriceTiersTtd(
  item: CostTierItem,
  usdToTtd: number | null,
  alt: AltUnitPricesUsd | null,
  vatRatePercent: number | null,
  /**
   * May a per-kg / per-lb figure be read out of the item NAME when the row
   * itself states no weight? OFF by default, and the two callers differ:
   *
   * - **The Master Catalogue grid: no.** A row stating no weight printed a
   *   per-kg and a per-lb anyway, off a number found in the product name, and
   *   the client asked how it could possibly know (USC-BUG-0048, Balgo 505636).
   *   The server refuses the same guess (TECH_DEBT #101). The cell reads "—"
   *   and the row surfaces under "Needs packaging review".
   * - **The quotation catalogue popup: yes.** #309 and #135 put that option
   *   there ON PURPOSE. A hollow-pack row (a TIN or DRUM the catalogue can say
   *   nothing about) otherwise offers only "Per Pack" at the CASE price — a
   *   case price under a pack heading, which is worse than a name-read kilo.
   *   There the figure is a priced OPTION someone picks and can see beside the
   *   case row, not a fact asserted in a grid cell.
   *
   * Same arithmetic, different question: the grid states what the row IS, the
   * popup offers what it COULD be quoted as.
   */
  { weightFromName = false }: { weightFromName?: boolean } = {}
): CostTiersTtd {
  // REFUSE FIRST, before any stored tier is even read. A `vat_applicable`
  // row with no Settings rate to gross at has no honest figure at ANY unit —
  // but until this guard existed, only the DERIVED branch (`withVat`, a few
  // lines down) refused. The STORED branches (`storedCaseTtd`, the stored
  // `ttPerLevel1`/`ttPerKg`/`ttPerLb`/`ttPerL`) read `priceLevels` verbatim,
  // with no VAT step of their own to skip — so a row with a stored tier
  // still answered it, at whatever VAT state the row was in when that tier
  // was last written. A row saved `vat_applicable: true` with the rate not
  // yet loaded on THIS screen offered its old stored price anyway (audit
  // finding, 2026-09-25) — the exact "gross at nothing" this file's other
  // refusals already exist to stop, one branch further along than they
  // reached. `catalogueRowNeedsUnsetVatRate` is this same test, exported so
  // `itemLookupRows.ts` and `QuotationDetailPage` ask the OWNER instead of
  // repeating the boolean.
  if (catalogueRowNeedsUnsetVatRate(item, vatRatePercent)) {
    return { case: null, pack: null, kg: null, lb: null, l: null, gal: null, packBasis: null };
  }

  const isTtd = catalogueRowIsTtd(item);
  // For a NAME-DERIVED figure only (below) — not `item_price` itself, which
  // goes through the one owner, `catalogueRowCostPrice`, instead. Grossing
  // before or after a currency conversion is the same number either way
  // (multiplication commutes), so both this and `catalogueRowCostPrice` read
  // the same passed-in Settings rate and neither hardcodes 1.125. `null`
  // means "cannot gross this", not "gross at 0%" — a VAT-applicable row with
  // no rate loaded returns no figure at all, same refusal as `catalogueRowCostPrice`.
  const withVat = (amount: number): number | null => {
    if (!item.vat_applicable) return amount;
    if (typeof vatRatePercent !== 'number' || !Number.isFinite(vatRatePercent)) return null;
    return amount * (1 + vatRatePercent / 100);
  };

  // ── PER CASE ───────────────────────────────────────────────────────────────
  // Stored tier first; otherwise work in TT$ directly rather than detouring
  // through USD, which rounded up at both hops and put this column a few cents
  // above `Per Case (TT$, w/ VAT)`.
  //
  // A collapsed group row (`getGroupedItems`) states its price once, as
  // `finalPrice`: TT$ and VAT-inclusive already (read from its members' stored
  // tiers). It has no `priceLevels` and its `item_price` is that same gross
  // figure, so deriving from `item_price` below would gross it a second time
  // (BUG_LIST_2026-10-12 #4, D117.4). It reads `finalPrice` and derives nothing.
  let caseTtd = item.isGroup ? positive(item.finalPrice) : storedCaseTtd(item);
  if (caseTtd == null && !item.isGroup) {
    // Grossed for VAT here, in whichever currency the row states it (VAT
    // does not care which currency it is billed in) — `catalogueRowCostPrice`.
    const p = catalogueRowCostPrice(item, vatRatePercent);
    if (p != null) {
      // A USD row with no rate yet has no TT$ figure at all.
      caseTtd = isTtd ? p : usdToTtd == null ? null : p * usdToTtd;
    }
  }

  // ── PER PACK / KG / LB ─────────────────────────────────────────────────────
  // Stored TT$ tiers first, for the same reason the case tier prefers its own —
  // but only a stored tier that is actually a PACK price. See `statesAPack`.
  //
  // A PACK COSTS LESS THAN THE CASE IT COMES IN. A figure that equals or exceeds
  // the case figure is not a per-pack price, and there are three ways to get one:
  //
  //   - `computeCostLevels` writes `ttPerLevel2 = ttPerLevel1 * level1PerLevel2`,
  //     and `level1PerLevel2` is 1 on every row whose pack count nobody stated
  //     (the vendor sync only has one when a reviewer filled in the split
  //     quantities). On those rows the two stored tiers are the SAME NUMBER, and
  //     preferring the stored one printed the case price under a "Per Pack"
  //     heading — the exact confident-wrong-number this module's derived branch
  //     already refuses by demanding a pack count. The stored branch bypassed the
  //     refusal because `ttPerLevel1` is positive either way. That is why the
  //     screen showed Cost Price, Per Case and Per Pack as one repeated figure.
  //   - a stated pack count of 1 divides the case price by one and reaches the
  //     same place from the derived side.
  //   - the stored tiers are a DENORMALISED COPY of the row's price, and until
  //     TECH_DEBT #329 was closed (2026-09-08) nothing restated them when a
  //     non-`manual` row's price was edited — so all four described the OLD price
  //     while `item_price` said the new one. That is fixed at the write now, and
  //     this refusal STAYS: it costs nothing, it is the only thing standing
  //     between a reader and any row already carrying a leftover from before the
  //     fix, and a pack dearer than its own case is not a number to print.
  //
  // "This row states no pack" is the honest answer to all three, and it is the
  // answer this module was written to give.
  const packCount = positive(item.packFacts?.packCount);
  const derivedPack = caseTtd != null && packCount != null ? caseTtd / packCount : null;
  const storedPack = positive(item.priceLevels?.ttPerLevel1);
  const statesAPack = (v: number | null): number | null =>
    v != null && (caseTtd == null || v < caseTtd) ? v : null;
  const packTtd = statesAPack(storedPack) ?? statesAPack(derivedPack);

  // The size of ONE pack. `unit_size` is the hand-entry column and `packFacts`
  // carries what the row states when nobody typed one — same two sources, same
  // order, as the server's own reader. A mass prices per kg and a volume per
  // litre, and one is never read as the other (`readPackSize`).
  const sizeText = item.unit_size || item.packFacts?.unitSize;
  const size = readPackSize(sizeText);
  // A row counted in ONE item (PCS, BTL, JAR ...) that states no pack is priced
  // per that item, so its own figure is what the size divides (MC-2: HADCO's
  // "Creamery Strawberry 1 Gallon" at TT$56.25 / PCS is TT$14.0625 a litre,
  // USC-BUG-0018). Per Pack still reads "—" on such a row (O-11); only the size
  // division uses it. A CASE / BOX row with no count stays undivided: its size is
  // the size of one pack inside it, and the count is the missing fact.
  //
  // ONLY when the size is exactly one number and one unit ("1 GAL", "725 ML" —
  // `unitVocabulary.splitSizeText`). `readPackSize` reads one measure out of any
  // text, so "12x1L", "6/1.9L", "1L x 12" or "12PK x 355ML" had their whole price
  // put over one bottle: TT$120 / L for a TT$10 / L case of twelve. Whether the
  // price buys one item or the count is the question D40 says is never guessed,
  // and a layout list would always miss the next spelling, so any size that is
  // not a bare measure answers "—".
  const oneItemTtd =
    packTtd ?? (isSingleItemUnit(item.unit) && splitSizeText(sizeText) != null ? caseTtd : null);
  const perPackSize = (dimension: PackSize['dimension']): number | null =>
    oneItemTtd != null && size != null && size.dimension === dimension ? oneItemTtd / size.amount : null;
  const derivedKg = perPackSize('mass');
  const sizeL = perPackSize('volume');
  // An ounce size fills the row's own litres per ounce (its rows, then "1 <unit> = <size>"), else
  // the water's, after every stated litre, for L and gal alike (D170.4).
  const ounceSizeRows =
    size?.ounces != null && isSingleItemUnit(item.unit)
      ? [{ fromQty: 1, fromUnit: String(item.unit), toQty: size.ounces, toUnit: 'OZ' }]
      : [];
  const sizeOunceL =
    size?.ounces != null
      ? size.ounces * ounceLitres({ litrePerOunce: statedLitrePerOunce(item.unitConversions, ounceSizeRows) })
      : null;
  const derivedL = sizeL ?? (oneItemTtd != null && sizeOunceL ? oneItemTtd / sizeOunceL : null);

  // ── LAST RESORT: the pack size read out of the item NAME ───────────────────
  // USD-native and VAT-exclusive, so it is grossed up and converted here.
  const fromUsd = (usd?: number | null): number | null => {
    if (usd == null || !Number.isFinite(usd) || usdToTtd == null) return null;
    const grossed = withVat(usd);
    return grossed == null ? null : ceilPrice(grossed * usdToTtd);
  };

  const packFromName = packTtd == null ? fromUsd(alt?.perInnerUsd) : null;

  // ── ONE ROW, ONE DIMENSION (MC-2, D28 / D36) ───────────────────────────────
  // A row whose size reads as a volume is a liquid: per L and per gal, and no
  // per kg or per lb whatever a stored tier or the name reading says — those are
  // the 1 L = 1 kg figures USC-BUG-0019 circled. A mass size is the mirror image,
  // except an ounce size, which also fills litres (D170.4). With no readable
  // size each pair shows what the row itself states, and the screen prints the unit beside every figure.
  const isLiquid = size?.dimension === 'volume';
  const isSolid = size?.dimension === 'mass' && size.ounces == null;

  // The name reading, only where the caller asked for it — see `weightFromName`.
  const nameKgTtd = weightFromName ? fromUsd(alt?.perKgUsd) : null;
  const nameLbTtd = weightFromName ? fromUsd(alt?.perLbUsd) : null;

  // Off by default. That last resort read a weight out of the item NAME
  // ("20x500G", "…5KG") and divided the row's price by it, so a row stating no weight at all
  // still printed a per-kg and a per-lb figure: "HOW DID IT CALCULATE THE KG & LB PRICE
  // WITHOUT THE CONVERSION DATA?" (USC-BUG-0048, prod, Balgo 505636). The server has always
  // refused this — TECH_DEBT #101, "a size guessed out of supplier prose would set the
  // divisor for a client-facing per-kg price" — and `computeCostLevels` stores null instead.
  // The screen was the one place still guessing. With no weight the cell reads "—" and the
  // row surfaces under "Needs packaging review", which is the honest route to getting the
  // weight typed in (D51.2: no ratio, no price). The name reading stays on Per Pack, where
  // it is labelled `packBasis: 'item_name'`, and on the quotation Equiv. columns, which
  // reach `perKgUsd` by their own entry point.
  const kgTtd = isLiquid ? null : positive(item.priceLevels?.ttPerKg) ?? derivedKg ?? nameKgTtd;

  // ── PER LB IS PER KG, THROUGH THE ONE POUND ────────────────────────────────
  // Never a second, independently sourced figure. Every writer that could put
  // one on this row derives it from the kg figure anyway — `computeCostLevels`
  // (`lbRaw = kgRaw * KG_PER_LB`), the name reading
  // (`perLbUsd = perKgUsd * LB_PER_KG`), and the division by pack size this
  // replaces — so a stored `ttPerLb` carries no fact the kg figure does not,
  // and preferring it carries one thing extra: WHICH POUND it was written with.
  // The pound moved to USC's 1 kg = 2.2 lb on 2026-09-07 (faa4a58), then 2.21 on 2026-09-24 (D62.1); every tier
  // stored before 2026-09-07 is on the avoirdupois 2.204623 and those from then to
  // 2026-09-24 on 2.2, and the column served
  // both at once — a per-kg and a per-lb cell on the same row that do not
  // reconcile, on a screen whose whole job is to be checkable by hand.
  // Reading it through `LB_PER_KG` is what makes the column state one pound.
  //
  // The stored figure stays as the last resort for a row that has no per-kg
  // figure at all, which is the only case where it says anything new.
  // Same gate on the pound, for the reason above it (USC-BUG-0048): a per-lb figure
  // derived from a weight nobody stated is the same fabrication one unit along.
  const lbTtd = isLiquid
    ? null
    : kgTtd != null
      ? kgTtd * LB_PER_KG
      : positive(item.priceLevels?.ttPerLb) ?? nameLbTtd;

  // ── PER LITRE ──────────────────────────────────────────────────────────────
  // The same three sources, in the same order, as per kg: the stored tier
  // (`costEngine.computeCostLevels`, off the row's volume size), then the pack
  // price over the pack's litres, then the name reading. It was stored-tier-only
  // while this file's size reader turned a litre into a kilogram — the litre went
  // to Per kg instead, which the owner had stopped on 2026-09-14 (D36). A gallon
  // is four litres (`packConversion.LITRES_PER_GALLON`), the same four
  // `itemGroup.parseSize` reads on the server.
  //
  // Second source since MC-2: the TT$ w/ VAT litre price the server stores in
  // `priceLevels.byUnit` off the item's own conversions — "1 PCS = 4 L" typed on
  // the Edit Item form priced no litre before (USC-BUG-0018).
  //
  // Only where the server priced `byUnit` in the currency this module reads the
  // row in. `graphPriceLevelsFor` treats every currency but a stated USD as TT$;
  // `catalogueRowIsTtd` reads a blank currency as US$ unless the row came from a
  // vendor invoice. On a blank-currency manual row the two disagree by the rate:
  // a TT$65.00 case beside a "TT$2.50" litre that was really US$2.50.
  const byUnitReadsAsRow = (String(item.currency || '').trim().toUpperCase() !== 'USD') === isTtd;
  const byUnitTt = (unit: string): number | null =>
    byUnitReadsAsRow
      ? positive((item.priceLevels?.byUnit || []).find((e) => canonicalUnit(e?.unit) === unit)?.ttAmount)
      : null;
  const lTtd = isSolid
    ? null
    : positive(item.priceLevels?.ttPerL) ?? byUnitTt('L') ?? derivedL ?? fromUsd(alt?.perLitreUsd);

  // ── PER GALLON IS FOUR LITRES, NEVER FOUR ROUNDED LITRES ───────────────────
  // Four litres (`packConversion.LITRES_PER_GALLON`). The stored `ttPerL` and
  // the byUnit `L` are rounded to the cent when written, and four times a
  // rounded litre is four times its error: a TT$56.25 gallon tub stores 14.06 a
  // litre and printed TT$56.24 / gal beside its own TT$56.25 price. So the gallon
  // comes off an UNROUNDED source first — the division by the size, then the
  // byUnit `GAL` entry (the server's measure star rounds it from the raw figure)
  // — and off the litre only when neither exists.
  const galTtd = isSolid
    ? null
    : sizeL != null
      ? sizeL * LITRES_PER_GALLON
      : byUnitTt('GAL') ?? (lTtd == null ? null : lTtd * LITRES_PER_GALLON);

  return {
    case: caseTtd,
    pack: packTtd ?? packFromName,
    kg: kgTtd,
    lb: lbTtd,
    l: lTtd,
    gal: galTtd,
    packBasis:
      statesAPack(storedPack) != null
        ? 'stored_tier'
        : statesAPack(derivedPack) != null
          ? 'row_pack_count'
          : packFromName != null
            ? 'item_name'
            : null,
  };
}

/**
 * WHY THE PER PACK CELL IS BLANK, in words for the cell's hover.
 *
 * NEW-8 (USC-BUG-0013 / 0029): two reports circled a "—" under Per Pack, on a
 * row priced per PCS and one priced per PACK, and asked for the row's own price
 * there. The blank is deliberate and it STAYS (`docs/plans/DECISIONS.md` D29,
 * PROVISIONAL O-11) — this only says why. It reads the tiers
 * `costPriceTiersTtd` already returned, so it cannot move a figure, and it is
 * null whenever the cell has one.
 *
 * The reasons follow the order the tiers refuse a pack price in.
 */
export function perPackBlankReason(item: CostTierItem, tiers: CostTiersTtd): string | null {
  if (tiers.pack != null) return null;
  if (tiers.case == null) {
    return (
      'There is no Per Case price on this row (no price yet, or a US$ price while the exchange rate loads), ' +
      'so there is no pack price to work out.'
    );
  }
  // A row priced per PACK, or per ONE item, already shows that unit's price under
  // Per Case. Printing it again is the repeated-figure grid the owner removed on
  // 2026-09-08 ("they are mostly same"). Spellings go through the vocabulary.
  const unit = canonicalUnit(item.unit);
  if (unit && (unit === 'PACK' || isSingleItemUnit(unit))) {
    return (
      `This row is priced per ${unit}, so Per Case already shows the price of one ${unit}. ` +
      'Per Pack is left blank rather than repeat that price.'
    );
  }
  // Any pack count above 1 divides the case into something cheaper (see
  // `statesAPack`), so a stated count that still left the cell blank is 1 or less.
  const packCount = positive(item.packFacts?.packCount);
  if (packCount != null) {
    return (
      `This row states ${packCount} pack per case, so one pack would cost the whole case price. ` +
      'Per Pack is left blank rather than repeat it.'
    );
  }
  return (
    'This row does not say how many packs are in a case, so the case price is not divided. ' +
    'Guessing a count would print a wrong price.'
  );
}

/** One unit a catalogue row can honestly be priced at, in both currencies. */
export interface CostUnitOption {
  key: CostUnit;
  /** What the selector reads. Never says "Case" over a figure that is not one. */
  label: string;
  /**
   * TT$, VAT-INCLUSIVE — the same figure, to the printed cent, that the Master
   * Catalogue screen shows for this row at this unit.
   */
  ttdWithVat: number;
  /**
   * TT$, VAT-INCLUSIVE, and null unless the row STATES its price in TT$.
   *
   * Owner rule (Deborah's VAT report, 2026-09-24): a quotation line's COST is
   * always the final, VAT-inclusive price USC actually pays — input VAT is
   * cost. This is NOT output VAT; a vessel is never charged VAT on anything
   * it is quoted or billed (D53), and that is a different rule with a
   * different owner (`lineTax.resolveVatAmount`), untouched here.
   *
   * This used to divide `ttdWithVat` back down by the VAT factor — inherited,
   * never an owner decision — so the item-lookup panel SHOWED the
   * VAT-inclusive TT$ but APPLIED a VAT-exclusive cost to the line: bulk
   * streaky bacon at TT$55.44/kg (VAT-exclusive) priced the line at 55.44
   * instead of the 62.37 USC actually pays.
   *
   * This is the figure a quotation line pins as `unitPriceTtd`
   * (`utils/pricing.unitCostTtd`) — the TT$ it was priced from, kept because
   * deriving it back out of the stored US$ cent cannot return it: TT$345.00 at
   * 6.5 is US$53.0769, stored ceiled at US$53.08, and read back as TT$345.02.
   * Null for a US$-priced row, where the derivation IS the honest answer.
   */
  ttdCost: number | null;
  /** US$, VAT-INCLUSIVE — what the quotation line stores as `unitPrice`. See `ttdCost` above for why. */
  usd: number;
  /**
   * The same US$, VAT-INCLUSIVE and UNROUNDED — what a quotation line's
   * conversion anchor holds. `usd` is ceiled to the cent, and an anchor
   * seeded from it ratchets on the next Unit change: TT$2.00 a litre at 6.00
   * is US$0.3333, stored US$0.34, and restated to a CASE of 12 L it read
   * US$4.08 against the row's US$4.00 (QUO-00054).
   */
  usdExact: number;
  /** The unit a line priced at this option is quoted in. */
  uom: string;
}

/**
 * THE UNITS A CATALOGUE ROW MAY BE ADDED TO A QUOTATION AT, AND WHAT EACH COSTS.
 *
 * The same four tiers the Master Catalogue screen prints, turned into the
 * options a selector can offer — so the price the quotation search popup hands
 * to a line is, by construction, the price the catalogue screen shows for that
 * row and unit. Two screens disagreeing about one row is the defect shape this
 * codebase keeps paying for; sharing the computation is the only fix that holds.
 *
 * A tier the row cannot state is NOT OFFERED. That is the point: the popup used
 * to offer "Per Pack" whenever `getAlternateQuotationPricing` could pattern-match
 * a pack size out of the item NAME, which is null on ~98% of the catalogue
 * (vendor-invoice rows carry their packaging in fields, not in the name) — so
 * the option was missing where the row plainly stated a pack, and where it did
 * appear it described the name rather than the row. An option that silently
 * prices at the CASE rate under a "Per Pack" label is worse than no option: it
 * is a wrong number on a client quotation, and nothing on screen says so.
 *
 * @param usdToTtd — the rate the LINE will be priced at (a quotation's own
 *                   locked rate), never today's and never a fallback. Null
 *                   returns no options at all: there is no honest US$ figure to
 *                   put on a line without it, and this popup once defaulted to
 *                   6 while the business rate had been 6.50 since 2026-08-06.
 * @param vatRatePercent — the Settings VAT rate (`getVatRate`), percent, or
 *                   null. See `costPriceTiersTtd`'s own doc comment — a
 *                   `vat_applicable` row offers no tier at all while this is
 *                   null, the same refusal a missing `usdToTtd` already gets.
 */
/**
 * A VAT-inclusive TT$ figure into the four money fields a `CostUnitOption`
 * states. The applied cost IS the VAT-inclusive figure — input VAT is cost
 * (D63; see `ttdCost`'s doc comment above), so nothing divides it back out.
 * Converted to US$ at the caller's own rate and ceiled ONCE, on the unrounded
 * tier — the US$ pair is `fxDisplay.usdCostFacts` (ceiled once, exact kept; D85.6), so
 * a figure does not ratchet a cent per hop.
 *
 * Extracted from `costUnitOptions`'s own `offer` closure (2026-09-24, D64) so
 * `itemLookupRows.catalogueCostUnitOptions` can price the units
 * `priceLevels.byUnit` names beyond this function's own fixed six — the
 * item's own conversions may reach PCS, ML or a custom PACK unit none of
 * `case`/`pack`/`kg`/`lb`/`l`/`gal` name — through the SAME call rather than a
 * second copy of this arithmetic. `byUnit` is stored VAT-inclusive
 * (`priceLevels.vatInclusive`), the same basis as the tiers.
 */
export function costUnitMoneyFromTtdWithVat(
  ttdWithVat: number,
  opts: { isTtd: boolean; usdToTtd: number }
): { ttdWithVat: number; ttdCost: number | null; usd: number; usdExact: number } {
  // The US$ pair is `usdCostFacts`'s - one owner of "TT$ to a line's cost + exact" (D85.6).
  const facts = usdCostFacts(ttdWithVat, 'TTD', 1 / opts.usdToTtd);
  if (!facts) throw new Error('costUnitMoneyFromTtdWithVat needs a finite, positive usdToTtd rate');
  return {
    ttdWithVat: ceilPrice(ttdWithVat),
    ttdCost: opts.isTtd ? ceilPrice(ttdWithVat) : null,
    usd: facts.unitPrice,
    usdExact: facts.unitPriceExact,
  };
}

/**
 * A genuine, currency-native US$ figure into the four money fields a
 * `CostUnitOption` states — `costUnitMoneyFromTtdWithVat`'s mirror image,
 * for a source whose OWN native money is US$ rather than TT$.
 *
 * `ttdCost` is always null: this row does not STATE a TT$ figure (it prices
 * in US$), so the TT$ side is DERIVED, on the same "never invent a stated
 * fact" terms `costUnitMoneyFromTtdWithVat`'s own `ttdCost` refuses on for a
 * US$ row (`opts.isTtd` false there). "Round in, ceil out" once, same as that
 * function: `usdExact` is the unrounded anchor, `usd`/`ttdWithVat` are each
 * ceiled ONCE off it, never chained off an already-rounded figure.
 *
 * Extracted (2026-09-25, F8) out of `itemLookupRows.graphUnitMoney`, which
 * was reimplementing this same "one native figure, one conversion, ceil once"
 * arithmetic by hand for a US$-priced `priceLevels.byUnit` entry — this
 * codebase's #1 historical defect shape (CLAUDE.md: "duplicated num()s and
 * unit normalizers"), here almost happening to the money-conversion owner
 * right beside it instead. `graphUnitMoney` still decides WHICH of a byUnit
 * entry's two stored figures (`ttAmount`/`usdAmount`) is this row's genuine,
 * currency-native one (invariant 5 — the other bakes in the FX rate current
 * when the row was SAVED, not this quotation's own) and calls whichever of
 * this function or `costUnitMoneyFromTtdWithVat` matches; this function only
 * does the arithmetic once handed the right figure.
 */
export function costUnitMoneyFromUsd(
  usdExact: number,
  opts: { usdToTtd: number }
): { ttdWithVat: number; ttdCost: number | null; usd: number; usdExact: number } {
  return {
    ttdWithVat: ceilPrice(usdExact * opts.usdToTtd),
    ttdCost: null,
    usd: ceilPrice(usdExact),
    usdExact,
  };
}

export function costUnitOptions(
  item: CostTierItem,
  usdToTtd: number | null,
  alt: (AltUnitPricesUsd & { innerUnitLabel?: string | null }) | null,
  vatRatePercent: number | null
): CostUnitOption[] {
  if (usdToTtd == null || !Number.isFinite(usdToTtd) || usdToTtd <= 0) return [];

  // #309 / #135: the popup KEEPS the name reading. A hollow-pack row that offers
  // only "Per Pack" at the case price is the worse answer — see `weightFromName`.
  const tiers = costPriceTiersTtd(item, usdToTtd, alt, vatRatePercent, { weightFromName: true });
  const isTtd = catalogueRowIsTtd(item);
  const options: CostUnitOption[] = [];

  const offer = (key: CostUnit, label: string, uom: string) => {
    const ttdWithVat = tiers[key];
    if (ttdWithVat == null || !Number.isFinite(ttdWithVat) || ttdWithVat < 0) return;
    // The APPLIED cost is the SAME VAT-inclusive tier the screen shows — input
    // VAT is cost (D63). See `costUnitMoneyFromTtdWithVat`.
    options.push({
      key,
      label,
      uom,
      ...costUnitMoneyFromTtdWithVat(ttdWithVat, { isTtd, usdToTtd }),
    });
  };

  // The row's own base unit. Named after the unit the row STATES when that is
  // not a case — a catch-weight row is billed per pound and has no case price,
  // and "Per Case" over its per-pound figure is the heading the owner reported.
  const suffix = baseUnitSuffix(item);
  offer('case', suffix ? `Per ${suffix}` : 'Per Case', item.unit || 'case');
  // The name-reading's own sub-label belongs only to a figure the name-reading
  // produced — same rule as the catalogue screen's Per Pack cell.
  offer(
    'pack',
    tiers.packBasis === 'item_name' && alt?.innerUnitLabel
      ? `Per Pack (${alt.innerUnitLabel})`
      : 'Per Pack',
    'pack'
  );
  offer('kg', 'Per kg', 'kg');
  offer('lb', 'Per lb', 'lb');
  // The screen's Per L column (prod USC-BUG-0018), offered the same way. A litre
  // row used to be offered "Per kg" here, at its price per litre (D36).
  offer('l', 'Per L', 'L');
  // And its gallon (MC-2), four litres; a quotation line takes GAL (D36).
  offer('gal', 'Per gal', 'GAL');

  return options;
}

/**
 * How a TT$ figure from this file is PRINTED: ceiled to the cent, "—" when
 * there is none. The Master Catalogue grid's Per Case / Per Pack / Per kg
 * cells and the vendor-invoice sync preview's price block both print through
 * this, so the card shows the figure the grid will show — it was a local
 * `formatTtd` inside `MasterCataloguePage`, and the sync card copied it
 * (2026-09-25).
 */
export function formatCostTierTtd(amount?: number | null): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return `TT$${ceilPrice(amount).toFixed(2)}`;
}
